import { useMutation, type Query } from '@tanstack/react-query';
import { graphql } from '~/graphql';
import type {
  UfpInventoryRunQuery,
  UfpInventoryRunResultQuery,
  UfpInventoryRunStatus,
} from '~/graphql/graphql';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import { graphqlRequest } from '~/lib/graphql-client';

const UFP_INVENTORY_RUN_QUERY_KEY = 'ufpInventoryRun';

export const RUN_POLL_INTERVAL_MS = 3000;
export const RUN_POLL_INTERVAL_AFTER_FAILURE_MS = 15000;

const startUfpInventoryRunMutation = graphql(`
  mutation StartUfpInventoryRun($input: StartUfpInventoryRunInput!) {
    startUfpInventoryRun(input: $input) {
      id
    }
  }
`);

const ufpInventoryRunQuery = graphql(`
  query UfpInventoryRun($id: ID!) {
    ufpInventoryRun(id: $id) {
      id
      status
      branchCodes
      weeksToLookBack
      apiVersion
      queuedAt
      startedAt
      finishedAt
      ufpHttpStatus
      failureDetail
    }
  }
`);

const ufpInventoryRunResultQuery = graphql(`
  query UfpInventoryRunResult($id: ID!) {
    ufpInventoryRunResult(id: $id) {
      locations {
        branchCode
        locationCode
        asOfTimestampUtc
        matchedLocations {
          id
          name
          organizationName
        }
        items {
          productCode
          description
          length
          outcome
          productName
          lengthName
          availablePcs
          inboundPcs
          onOrderPcs
          requestedPcs
          avgWeeklyUsagePcs
        }
      }
    }
  }
`);

const ufpInventoryRunRawResponseUrlQuery = graphql(`
  query UfpInventoryRunRawResponseUrl($id: ID!) {
    ufpInventoryRunRawResponseUrl(id: $id)
  }
`);

export type UfpInventoryRun = NonNullable<
  UfpInventoryRunQuery['ufpInventoryRun']
>;

export type UfpInventoryRunLocation = NonNullable<
  UfpInventoryRunResultQuery['ufpInventoryRunResult']['locations']
>[number];

export type UfpInventoryRunItem = UfpInventoryRunLocation['items'][number];

/** `isRefreshFailing`: the run on screen is the last one loaded, and the latest poll failed. */
export type FollowedUfpInventoryRun =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'loadError' }
  | { kind: 'notFound' }
  | { kind: 'run'; run: UfpInventoryRun; isRefreshFailing: boolean };

export type UfpInventoryRunResult =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'unreadable' }
  | { kind: 'locations'; locations: UfpInventoryRunLocation[] };

export function isInProgress(status: UfpInventoryRunStatus): boolean {
  return status === 'QUEUED' || status === 'RUNNING';
}

/**
 * The server ends every run, failing one that passes its time limit, so the page polls until the
 * run reports an outcome and keeps no deadline of its own. A failed refetch keeps polling, more
 * slowly: nothing else restarts the interval, so one 502 would otherwise freeze a running run on
 * screen.
 */
export function runPollIntervalOf(
  query: Pick<Query<UfpInventoryRunQuery>, 'state'>
): number | false {
  const run = query.state.data?.ufpInventoryRun;
  if (run == null || !isInProgress(run.status)) {
    return false;
  }
  return query.state.status === 'error'
    ? RUN_POLL_INTERVAL_AFTER_FAILURE_MS
    : RUN_POLL_INTERVAL_MS;
}

export const useStartUfpInventoryRun = () =>
  useGraphqlMutation(startUfpInventoryRunMutation);

export function useUfpInventoryRun(
  runId: string | undefined
): FollowedUfpInventoryRun {
  const query = useGraphqlQuery(
    ufpInventoryRunQuery,
    [UFP_INVENTORY_RUN_QUERY_KEY, runId],
    { id: runId ?? '' },
    {
      enabled: runId !== undefined,
      staleTime: 0,
      // Interval polling pauses in a hidden tab, so catch up as soon as it is shown again.
      refetchOnWindowFocus: true,
      refetchInterval: runPollIntervalOf,
    }
  );

  if (runId === undefined) {
    return { kind: 'idle' };
  }
  if (query.data === undefined) {
    return { kind: query.isError ? 'loadError' : 'loading' };
  }
  const run = query.data.ufpInventoryRun;
  if (run == null) {
    return { kind: 'notFound' };
  }
  return { kind: 'run', run, isRefreshFailing: query.isError };
}

export function useUfpInventoryRunResult(runId: string): UfpInventoryRunResult {
  const query = useGraphqlQuery(
    ufpInventoryRunResultQuery,
    [UFP_INVENTORY_RUN_QUERY_KEY, runId, 'result'],
    { id: runId }
  );

  if (query.data === undefined) {
    return { kind: query.isError ? 'error' : 'loading' };
  }
  const { locations } = query.data.ufpInventoryRunResult;
  return locations == null
    ? { kind: 'unreadable' }
    : { kind: 'locations', locations };
}

export function useDownloadUfpInventoryRunRawResponse(onError: () => void) {
  const { mutate, isPending } = useMutation({
    mutationFn: (runId: string) =>
      graphqlRequest(ufpInventoryRunRawResponseUrlQuery, { id: runId }),
    onSuccess: (data) => {
      const url = data.ufpInventoryRunRawResponseUrl;
      if (/^https?:\/\//i.test(url)) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
    onError,
  });

  return { download: mutate, isDownloading: isPending };
}
